/**
 * RAG Add Page - Upload Documents
 * Upload files to the RAG system for document processing
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  IconButton,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  CloudUpload as CloudUploadIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';
import { toast } from 'sonner';
import api from '../../interceptors/interceptor';
import { API_ROUTES } from '../../constants/apiRoutes';
import { ROUTE_PATHS } from '../../constants/routePaths';

export function RagAddPage() {
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);

  // Validation schema for upload form
  const uploadValidationSchema = Yup.object({
    files: Yup.array()
      .min(1, 'At least one file is required')
      .required('Files are required')
      .test('fileSize', 'One or more files exceed 25MB', (value) => {
        if (!value || !Array.isArray(value)) return false;
        return value.every((file: File) => file.size <= 25 * 1024 * 1024); // 25MB
      })
      .test('fileType', 'One or more files have unsupported format', (value) => {
        if (!value || !Array.isArray(value)) return false;
        const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 
                           'text/plain', 'text/csv', 'application/json'];
        const validExtensions = ['.pdf', '.docx', '.txt', '.csv', '.json'];
        return value.every((file: File) => 
          validTypes.includes(file.type) || validExtensions.some(ext => file.name.toLowerCase().endsWith(ext))
        );
      }),
  });

  // Formik for upload form
  const uploadFormik = useFormik({
    initialValues: {
      files: [] as File[],
    },
    validationSchema: uploadValidationSchema,
    onSubmit: async (values) => {
      if (!values.files || values.files.length === 0) return;
      
      setIsUploading(true);
      try {
        // Create FormData for file upload
        const formData = new FormData();
        values.files.forEach((file) => {
          formData.append('files', file);
        });
        formData.append('auto_ingest', 'true');
        formData.append('embedding_mode', 'local');

        toast.info(`Uploading ${values.files.length} document(s)...`);
        
        // Call the API
        const response = await api.post(API_ROUTES.RAG_UPLOAD_PDF, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          // Skip the default Authorization interceptor for this request
          transformRequest: [(data, headers) => {
            // Ensure our Authorization header takes precedence
            headers['Authorization'] = `Bearer ${window.env.REACT_APP_LLM_API_KEY}`;
            return data;
          }, ...((api.defaults.transformRequest as any) || [])],
        });

        const responseData = response.data;
        console.log('📤 Upload response:', responseData);

        toast.success(responseData.message || `${values.files.length} document(s) uploaded successfully`);
        
        // Check if ingestion was triggered and we have a job_id
        if (responseData.ingestion_triggered && responseData.job_id) {
          console.log('🔄 Navigating to list page with job_id:', responseData.job_id);
          // Navigate to list page with job_id for SSE tracking
          uploadFormik.resetForm();
          navigate(ROUTE_PATHS.RAG, { state: { jobId: responseData.job_id } });
        } else {
          // No ingestion triggered, just navigate back
          uploadFormik.resetForm();
          setTimeout(() => {
            navigate(ROUTE_PATHS.RAG);
          }, 1500);
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to upload document';
        toast.error(errorMessage);
        setIsUploading(false);
      }
    },
  });

  const handleFilesSelect = (newFiles: FileList | File[]) => {
    const filesArray = Array.from(newFiles);
    const currentFiles = uploadFormik.values.files || [];
    uploadFormik.setFieldValue('files', [...currentFiles, ...filesArray]);
  };

  const handleRemoveFile = (index: number) => {
    const currentFiles = uploadFormik.values.files || [];
    const newFiles = currentFiles.filter((_, i) => i !== index);
    uploadFormik.setFieldValue('files', newFiles);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFilesSelect(files);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFilesSelect(files);
    }
  };

  const getTotalSize = () => {
    const files = uploadFormik.values.files || [];
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    return (totalBytes / 1024 / 1024).toFixed(2);
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(ROUTE_PATHS.RAG)}
            sx={{ mb: 2 }}
          >
            Back to RAG Files
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', mb: 2 }}>
            Upload Documents
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b' }}>
            Upload multiple documents to the RAG system for processing
          </Typography>
        </Box>

        {/* Upload Documents Section */}
        <Paper sx={{ borderRadius: 3, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)', p: 4 }}>
          <form onSubmit={uploadFormik.handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* File Upload */}
              <input
                id="file-upload"
                type="file"
                accept=".pdf,.docx,.txt,.csv,.json"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
              />
              <label htmlFor="file-upload">
                <Paper
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  sx={{
                    p: 6,
                    border: `2px dashed ${
                      uploadFormik.touched.files && uploadFormik.errors.files
                        ? '#dc2626'
                        : '#cbd5e1'
                    }`,
                    borderRadius: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: '#f8fafc',
                      borderColor: '#94a3b8',
                    }
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: '#64748b', mb: 2 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                    {uploadFormik.values.files && uploadFormik.values.files.length > 0
                      ? `${uploadFormik.values.files.length} file(s) selected`
                      : 'Drop files here or click to browse'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Supports PDF, DOCX, TXT, CSV, and JSON formats (max 25MB per file)
                  </Typography>
                  {uploadFormik.values.files && uploadFormik.values.files.length > 0 && (
                    <Typography variant="caption" sx={{ color: '#16a34a', display: 'block', mt: 1 }}>
                      Total: {getTotalSize()} MB
                    </Typography>
                  )}
                </Paper>
              </label>
              {uploadFormik.touched.files && uploadFormik.errors.files && (
                <Typography variant="caption" sx={{ color: '#dc2626', mt: -2 }}>
                  {typeof uploadFormik.errors.files === 'string' 
                    ? uploadFormik.errors.files 
                    : 'Please check the files'}
                </Typography>
              )}
              {/* Selected Files List */}
              {uploadFormik.values.files && uploadFormik.values.files.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {uploadFormik.values.files.map((file, index) => (
                    <Chip
                      key={index}
                      icon={<InsertDriveFileIcon />}
                      label={`${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`}
                      onDelete={() => handleRemoveFile(index)}
                      sx={{
                        bgcolor: '#eff6ff',
                        '& .MuiChip-deleteIcon': {
                          color: '#64748b',
                          '&:hover': {
                            color: '#dc2626',
                          },
                        },
                      }}
                    />
                  ))}
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button 
                  onClick={() => navigate(ROUTE_PATHS.RAG)}
                  disabled={isUploading}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  type="submit"
                  disabled={!uploadFormik.values.files || uploadFormik.values.files.length === 0 || isUploading}
                  sx={{ 
                    bgcolor: '#2563eb',
                    '&:hover': { bgcolor: '#1d4ed8' }
                  }}
                  startIcon={isUploading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
                >
                  {isUploading ? 'Uploading...' : `Upload${uploadFormik.values.files && uploadFormik.values.files.length > 0 ? ` (${uploadFormik.values.files.length})` : ''}`}
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}
